import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Menu, Notification, Profile, ArrowDown2, Setting4, Logout, Moon, Sun1, Buildings } from 'iconsax-react';
import { useTheme } from '../../context/ThemeContext';
import { getCompanyInfo } from '../../services/companyInfo';
import api from '../../services/api';
import BranchSwitcher from './BranchSwitcher';

const Navbar = () => {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [companyInfo, setCompanyInfo] = useState(null);
  const [hasNotifications, setHasNotifications] = useState(false);

  React.useEffect(() => {
    const fetchCompanyInfo = async () => {
      try {
        const info = await getCompanyInfo();
        setCompanyInfo(info);
      } catch (error) {
        console.error('Failed to fetch company info:', error);
      }
    };

    const checkNotifications = async () => {
      try {
        const response = await api.get('/notifications/rent_due.php?threshold=7');
        if (Array.isArray(response) && response.length > 0) {
          setHasNotifications(true);
        }
      } catch (error) {
        console.error('Failed to check notifications:', error);
      }
    };

    fetchCompanyInfo();
    checkNotifications();

    // Optional: Poll every 5 minutes
    const interval = setInterval(checkNotifications, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  return (
    <header className="fixed top-0 left-0 right-0 h-16 bg-white/80 backdrop-blur-md border-b border-neutral-200 z-30 dark:bg-neutral-900/80 dark:border-neutral-800">
      <div className="flex items-center justify-between h-full px-4 lg:px-6">
        {/* Left Side: Menu Toggle & Search */}
        <div className="flex items-center gap-4 flex-1">
          {/* Mobile Menu Toggle */}
          <button
            id="menu-toggle"
            className="lg:hidden text-neutral-600 hover:text-neutral-900 dark:text-neutral-300 dark:hover:text-white"
            onClick={() => {
              const sidebar = document.getElementById('sidebar');
              const overlay = document.getElementById('sidebar-overlay');
              if (sidebar) {
                if (sidebar.classList.contains('translate-x-full')) {
                  // Open sidebar
                  sidebar.classList.remove('translate-x-full');
                  sidebar.classList.remove('opacity-0');
                  if (overlay) {
                    overlay.classList.remove('opacity-0', 'pointer-events-none');
                    overlay.classList.add('opacity-100', 'pointer-events-auto');
                  }
                } else {
                  // Close sidebar
                  sidebar.classList.add('translate-x-full');
                  sidebar.classList.add('opacity-0');
                  if (overlay) {
                    overlay.classList.remove('opacity-100', 'pointer-events-auto');
                    overlay.classList.add('opacity-0', 'pointer-events-none');
                  }
                }
              }
            }}
          >
            <Menu size={24} variant="Outline" />
          </button>

          {/* Branding (Desktop) - Replaces Search Bar */}
          <div className="hidden md:flex items-center gap-3">
            {companyInfo?.logoBase64 ? (
              <img
                src={`data:${companyInfo.logoMimeType};base64,${companyInfo.logoBase64}`}
                alt="Logo"
                className="w-10 h-10 object-contain"
              />
            ) : (
              <div className="w-10 h-10 bg-primary-red/10 rounded-full flex items-center justify-center text-primary-red">
                {/* Fallback Icon if no logo */}
                <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2L2 7l10 5 10-5-10-5zm0 9l2.5-1.25L12 8.5l-2.5 1.25L12 11zm0 2.5l-5-2.5-5 2.5L12 22l10-8.5-5-2.5-5 2.5z" />
                </svg>
              </div>
            )}
            <div className="flex flex-col justify-center">
              <span className="text-body-medium font-bold text-neutral-800 dark:text-white leading-tight">
                {companyInfo?.companyName || 'ناوی کۆمپانیا'}
              </span>
            </div>
          </div>
        </div>

        {/* Right Side: Actions & User Menu */}
        <div className="flex items-center gap-4">
          {/* Branch Switcher for Owners */}
          <BranchSwitcher />

          {/* Company Management Link */}
          <button
            onClick={() => navigate('/companies')}
            className="p-2 text-neutral-600 hover:text-neutral-900 hover:bg-neutral-100 rounded-medium transition-colors dark:text-neutral-300 dark:hover:text-white dark:hover:bg-neutral-800"
            title="بەڕێوەبردنی کۆمپانیاکان"
          >
            <Buildings size={20} variant="Outline" />
          </button>

          {/* Theme Toggle */}
          <button
            onClick={toggleTheme}
            className="p-2 text-neutral-600 hover:text-neutral-900 hover:bg-neutral-100 rounded-medium transition-colors dark:text-neutral-300 dark:hover:text-white dark:hover:bg-neutral-800"
            title={theme === 'dark' ? 'Light Mode' : 'Dark Mode'}
          >
            {theme === 'dark' ? (
              <Sun1 size={20} variant="Outline" />
            ) : (
              <Moon size={20} variant="Outline" />
            )}
          </button>

          {/* Settings */}
          <button
            onClick={() => navigate('/settings')}
            className="p-2 text-neutral-600 hover:text-neutral-900 hover:bg-neutral-100 rounded-medium transition-colors dark:text-neutral-300 dark:hover:text-white dark:hover:bg-neutral-800"
            title="ڕێکخستنەکان"
          >
            <Setting4 size={20} variant="Outline" />
          </button>

          {/* Notifications Link */}
          <button
            onClick={() => navigate('/notifications')}
            className="p-2 text-neutral-600 hover:text-neutral-900 hover:bg-neutral-100 rounded-medium transition-colors dark:text-neutral-300 dark:hover:text-white dark:hover:bg-neutral-800 relative"
            title="ئاگادارکردنەوەکان"
          >
            <Notification size={20} variant="Outline" />
            {hasNotifications && (
              <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-error rounded-full ring-2 ring-white dark:ring-neutral-900 animate-pulse" />
            )}
          </button>

          {/* User Menu */}
          <div className="relative" id="user-menu-container">
            <button
              id="user-menu-button"
              className="flex items-center gap-3 p-2 hover:bg-neutral-100 rounded-medium transition-colors dark:hover:bg-neutral-800"
              onClick={() => setShowUserMenu(!showUserMenu)}
            >
              <div className="w-8 h-8 bg-primary-red/10 rounded-full flex items-center justify-center">
                <Profile size={16} className="text-primary-red" variant="Outline" />
              </div>
              <div className="hidden md:block text-right">
                <p className="text-body-small font-medium text-neutral-800 dark:text-white">
                  {user?.full_name || 'بەکارهێنەر'}
                </p>
                <p className="text-caption text-neutral-500 dark:text-neutral-400">
                  {user?.role || 'کارمەند'}
                </p>
              </div>
              <ArrowDown2 size={16} className="hidden md:block text-neutral-500 dark:text-neutral-400" variant="Outline" />
            </button>

            {/* User Dropdown Menu */}
            {showUserMenu && (
              <div className="absolute left-0 mt-2 w-48 bg-primary-white rounded-medium shadow-high border border-neutral-200 py-2 z-50 dark:bg-neutral-900 dark:border-neutral-800">
                <button onClick={() => navigate('/profile')} className="w-full flex items-center gap-3 px-4 py-2 text-body-medium text-neutral-700 hover:bg-neutral-100 dark:text-neutral-300 dark:hover:bg-neutral-800">
                  <Profile size={16} variant="Outline" />
                  <span>پڕۆفایل</span>
                </button>
                <a href="/settings" className="flex items-center gap-3 px-4 py-2 text-body-medium text-neutral-700 hover:bg-neutral-100 dark:text-neutral-300 dark:hover:bg-neutral-800">
                  <Setting4 size={16} variant="Outline" />
                  <span>ڕێکخستنەکان</span>
                </a>
                <div className="border-t border-neutral-200 my-2 dark:border-neutral-800"></div>
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center gap-3 px-4 py-2 text-body-medium text-error hover:bg-error/10 dark:hover:bg-error/20"
                >
                  <Logout size={16} variant="Outline" />
                  <span>دەرچوون</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Navbar;

